import { useState, useMemo } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Rating,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import ScienceIcon from '@mui/icons-material/Science';
import GavelIcon from '@mui/icons-material/Gavel';
import StarIcon from '@mui/icons-material/Star';
import EventIcon from '@mui/icons-material/Event';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import dayjs from 'dayjs';
import useAuthStore from '../../store/authStore';
import { fetchTutorProfile, updateTutorProfile } from '../../api/tutorProfile';
import { SUBJECT_OPTIONS } from '../../components/forms/StudentFormDialog';
import ReviewSortControls from '../../components/reviews/ReviewSortControls';
import sortReviews from '../../components/reviews/sortReviews';

const translateSubject = (val) => {
  if (!val) return '';
  const byValue = SUBJECT_OPTIONS.find((o) => o.value === val);
  if (byValue) return byValue.label;
  const byLabel = SUBJECT_OPTIONS.find((o) => o.label === val);
  if (byLabel) return byLabel.label;
  if (val.includes(',')) {
    const parts = val.split(',').map((s) => s.trim());
    const translated = parts.find((p) => SUBJECT_OPTIONS.find((o) => o.value === p || o.label === p));
    if (translated) {
      const opt = SUBJECT_OPTIONS.find((o) => o.value === translated || o.label === translated);
      return opt?.label || translated;
    }
  }
  return val;
};

const B = { primary: '#4357AD', dark: '#1E2A4A', green: '#D3FEC6', orange: '#FF521B' };
const cardSx = { borderRadius: 4, boxShadow: '0 2px 12px rgba(67,87,173,0.08)', bgcolor: '#fff' };

const profileSections = [
  { key: 'specialization', label: 'Спеціалізація', icon: <SchoolIcon />, color: '#8B5CF6' },
  { key: 'grades', label: 'Класи', icon: <SchoolIcon />, color: '#06B6D4' },
  { key: 'education', label: 'Освіта', icon: <AutoStoriesIcon />, color: '#F59E0B' },
  { key: 'experience', label: 'Досвід викладання', icon: <WorkIcon />, color: '#22C55E' },
];

const textSections = [
  { key: 'bio', label: 'Про себе', icon: <FormatQuoteIcon />, color: B.primary },
  { key: 'scientificInterests', label: 'Наукові інтереси', icon: <ScienceIcon />, color: '#8B5CF6' },
  { key: 'workRules', label: 'Правила роботи', icon: <GavelIcon />, color: '#EF4444' },
];

const allFields = [
  { key: 'subject', label: 'Предмет' },
  { key: 'specialization', label: 'Спеціалізація' },
  { key: 'grades', label: 'Класи' },
  { key: 'education', label: 'Освіта' },
  { key: 'experience', label: 'Досвід викладання' },
  { key: 'bio', label: 'Про себе', multiline: true },
  { key: 'scientificInterests', label: 'Наукові інтереси', multiline: true },
  { key: 'workRules', label: 'Правила роботи', multiline: true },
];

const TutorProfilePage = () => {
  const { user } = useAuthStore();
  const tutorId = user?.id;
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState(false);
  const [reviewSort, setReviewSort] = useState('newest');
  const [formData, setFormData] = useState({});

  const { data: profile, isLoading, error } = useQuery({
    queryKey: ['tutor-profile', tutorId],
    queryFn: () => fetchTutorProfile(tutorId),
    enabled: Boolean(tutorId),
    retry: false,
  });

  const updateMutation = useMutation({
    mutationFn: (payload) => updateTutorProfile(tutorId, payload),
    onSuccess: () => {
      enqueueSnackbar('Профіль оновлено', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['tutor-profile', tutorId] });
      setEditing(false);
    },
    onError: () => enqueueSnackbar('Не вдалося оновити профіль', { variant: 'error' }),
  });

  const handleEdit = () => {
    const data = {};
    allFields.forEach((f) => { data[f.key] = profile?.[f.key] || ''; });
    setFormData(data);
    setEditing(true);
  };

  const handleSave = () => updateMutation.mutate(formData);
  const handleChange = (key) => (e) => setFormData((prev) => ({ ...prev, [key]: e.target.value }));

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: B.primary }} /></Box>;
  }
  if (error) {
    return <Typography color="error">Помилка: {error?.response?.data?.message || error.message}</Typography>;
  }
  if (!profile) {
    return <Typography>Профіль не знайдено.</Typography>;
  }

  const completionRate = Math.round(profile.completionRate ?? 0);

  const initials = profile.tutorName
    ?.split(' ')
    .map((w) => w.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';

  // ────────────── EDIT MODE ──────────────
  if (editing) {
    return (
      <Stack spacing={2.5}>
        <Card sx={{ ...cardSx, background: `linear-gradient(135deg, ${B.dark} 0%, ${B.primary} 100%)` }}>
          <CardContent sx={{ py: 3, px: 3.5 }}>
            <Typography variant="h5" fontWeight={800} sx={{ color: '#fff' }}>
              Редагування профілю
            </Typography>
          </CardContent>
        </Card>
        <Card sx={cardSx}>
          <CardContent sx={{ py: 3 }}>
            <Grid container spacing={2.5}>
              {allFields.map((field) => (
                <Grid item xs={12} md={field.multiline ? 12 : 6} key={field.key}>
                  <TextField
                    label={field.label}
                    fullWidth
                    size="small"
                    value={formData[field.key] || ''}
                    onChange={handleChange(field.key)}
                    multiline={field.multiline}
                    rows={field.multiline ? 4 : 1}
                    sx={{
                      '& .MuiOutlinedInput-root': { borderRadius: 3 },
                    }}
                  />
                </Grid>
              ))}
            </Grid>
            <Stack direction="row" spacing={2} justifyContent="flex-end" mt={3}>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => setEditing(false)}
                sx={{ borderRadius: 3, textTransform: 'none' }}
              >
                Скасувати
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={updateMutation.isPending}
                sx={{ bgcolor: B.primary, '&:hover': { bgcolor: '#3648A0' }, borderRadius: 3, textTransform: 'none' }}
              >
                Зберегти
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    );
  }

  // ────────────── VIEW MODE ──────────────
  return (
    <Stack spacing={2.5}>
      {/* ── Hero Card: Avatar + Name + Speech Bubble ── */}
      <Card
        sx={{
          ...cardSx,
          background: `linear-gradient(135deg, ${B.dark} 0%, ${B.primary} 60%, #6C63FF 100%)`,
          overflow: 'visible',
          position: 'relative',
        }}
      >
        <CardContent sx={{ py: 4, px: { xs: 3, md: 5 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} spacing={3}>
            {/* Avatar side */}
            <Box sx={{ position: 'relative', flexShrink: 0, alignSelf: { xs: 'center', md: 'flex-start' } }}>
              <Avatar
                sx={{
                  width: 100,
                  height: 100,
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: 36,
                  border: '4px solid rgba(255,255,255,0.25)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                }}
              >
                {initials}
              </Avatar>
            </Box>

            {/* Name + subject + edit */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h4" fontWeight={800} sx={{ color: '#fff', mb: 0.5 }}>
                {profile.tutorName}
              </Typography>
              {profile.subject && (
                <Chip
                  label={translateSubject(profile.subject)}
                  sx={{
                    bgcolor: 'rgba(211,254,198,0.2)',
                    color: B.green,
                    fontWeight: 700,
                    fontSize: 13,
                    border: '1px solid rgba(211,254,198,0.3)',
                    mb: 1,
                  }}
                />
              )}
              {/* Quick stats row */}
              <Stack direction="row" spacing={{ xs: 2, sm: 3 }} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                {[
                  { value: profile.completedLessons ?? 0, label: 'Проведено' },
                  { value: profile.averageRating ? profile.averageRating.toFixed(1) : '—', label: 'Рейтинг' },
                ].map((s) => (
                  <Box key={s.label} sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight={800} sx={{ color: '#fff', lineHeight: 1 }}>
                      {s.value}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>
                      {s.label}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>

            {/* Edit button */}
            <IconButton
              onClick={handleEdit}
              sx={{
                alignSelf: { xs: 'flex-end', md: 'flex-start' },
                bgcolor: 'rgba(255,255,255,0.15)',
                color: '#fff',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
              }}
            >
              <EditIcon />
            </IconButton>
          </Stack>
        </CardContent>
      </Card>

      {/* ── Stats Cards Row ── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        {[
          { label: 'Проведених уроків', value: profile.completedLessons ?? 0, icon: <CheckCircleIcon />, color: '#22C55E', bg: '#D1FAE5' },
          { label: 'Середній рейтинг', value: profile.averageRating ? profile.averageRating.toFixed(1) : '—', icon: <StarIcon />, color: '#EF4444', bg: '#FEE2E2', extra: profile.averageRating > 0 && <Rating value={profile.averageRating} precision={0.1} readOnly size="small" sx={{ ml: 1 }} /> },
        ].map((s) => (
          <Card key={s.label} sx={{ ...cardSx, height: '100%' }}>
            <CardContent sx={{ py: 2.5, px: 2.5 }}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box sx={{ width: 48, height: 48, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: s.bg, color: s.color, flexShrink: 0 }}>
                  {s.icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" alignItems="center">
                    <Typography variant="h5" fontWeight={800} sx={{ color: B.dark }}>{s.value}</Typography>
                    {s.extra}
                  </Stack>
                  <Typography variant="caption" sx={{ color: '#8891A5', fontWeight: 500 }}>{s.label}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* ── Profile Info: Short fields ── */}
      {(() => {
        const filled = profileSections.filter((s) => profile[s.key]);
        if (!filled.length) return null;
        const cols = filled.length >= 4 ? 4 : filled.length;
        return (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: `repeat(${Math.min(cols, 2)}, 1fr)`, md: `repeat(${cols}, 1fr)` }, gap: 2 }}>
            {filled.map((section) => (
              <Card key={section.key} sx={{ ...cardSx, height: '100%' }}>
                <CardContent sx={{ py: 2.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                    <Box sx={{ color: section.color, display: 'flex' }}>{section.icon}</Box>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: B.dark }}>
                      {section.label}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.6 }}>
                    {profile[section.key]}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        );
      })()}

      {/* ── Profile Info: Text sections ── */}
      {textSections.map((section) => {
        const value = profile[section.key];
        if (!value) return null;
        return (
          <Card key={section.key} sx={cardSx}>
            <CardContent sx={{ py: 2.5 }}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: `${section.color}15`,
                    color: section.color,
                  }}
                >
                  {section.icon}
                </Box>
                <Typography variant="h6" fontWeight={700} sx={{ color: B.dark }}>
                  {section.label}
                </Typography>
              </Stack>
              <Typography variant="body1" sx={{ color: '#475569', whiteSpace: 'pre-line', lineHeight: 1.7, pl: 0.5 }}>
                {value}
              </Typography>
            </CardContent>
          </Card>
        );
      })}

      {/* ── Reviews ── */}
      <Card sx={cardSx}>
        <CardContent sx={{ py: 2.5 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#FEF3C7',
                color: '#F59E0B',
              }}
            >
              <StarIcon />
            </Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: B.dark }}>
              Відгуки
            </Typography>
            {profile.reviews?.length > 0 && (
              <Chip
                label={profile.reviews.length}
                size="small"
                sx={{ bgcolor: '#EDE9FE', color: B.primary, fontWeight: 700, fontSize: 12 }}
              />
            )}
          </Stack>
          {profile.reviews?.length > 1 && (
            <Box sx={{ mb: 2 }}>
              <ReviewSortControls value={reviewSort} onChange={setReviewSort} />
            </Box>
          )}
          {(!profile.reviews || profile.reviews.length === 0) ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#8891A5' }}>Відгуків поки немає.</Typography>
            </Box>
          ) : (
            <Stack spacing={2} divider={<Divider sx={{ borderColor: '#F1F5F9' }} />}>
              {sortReviews(profile.reviews, reviewSort).map((review) => (
                <Box key={review.id}>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: `${B.primary}15`,
                        color: B.primary,
                        fontSize: 13,
                        fontWeight: 700,
                      }}
                    >
                      {review.studentName?.charAt(0)?.toUpperCase()}
                    </Avatar>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: B.dark }}>
                      {review.studentName}
                    </Typography>
                    <Rating value={review.rating} readOnly size="small" />
                    <Typography variant="caption" sx={{ color: '#8891A5', ml: 'auto !important' }}>
                      {dayjs(review.createdAt).format('DD.MM.YYYY')}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ color: '#475569', pl: { xs: 0, sm: 5.5 }, lineHeight: 1.6 }}>
                    {review.text}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
};

export default TutorProfilePage;
